import { signOut as submit } from '@BUP/services/auth.js'
import { useReducer } from 'react'
import queryClient from '../reactQueryClient'
import { clearNamespace } from '@BUP/hooks/useLocalStorage'

const requestReducer = (state, action) => {
  switch (action.type) {
    case 'loading':
      return { loading: true, error: null, data: null }
    case 'success':
      return { loading: false, error: null, data: true }
    case 'error':
      return { loading: false, error: action.payload, data: false }
    default:
      return state
  }
}

const requestInitialState = { loading: false, error: undefined, success: undefined }

export function useSignOut () {
  const [state, dispatch] = useReducer(requestReducer, requestInitialState)

  const mutate = async (email, password) => {
    dispatch({ type: 'loading' })
    try {
      const data = await submit(email, password)
      // Cancelar queries activas y limpiar el cache de react-query
      try {
        // cancelar cualquier query en curso
        await queryClient.cancelQueries()
        // eliminar queries/cache guardadas
        // removeQueries with no args should remove all queries
        try {
          await queryClient.removeQueries()
        } catch (e) {
          // removeQueries puede no devolver promesa en algunas versiones; ignorar fallos
        }
        // clear como último recurso
        if (typeof queryClient.clear === 'function') queryClient.clear()
      } catch (e) {
        // no bloquear el logout si react-query falla
        // eslint-disable-next-line no-console
        console.warn('react-query cleanup failed:', e)
      }

      // Limpiar storages del navegador: eliminar keys namespaced si existe NAMESPACE
      try {
        if (typeof window !== 'undefined') {
          const NAMESPACE =
            (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STORAGE_NAMESPACE) ||
            'BUP'
          // eliminar solo keys de la app (prefijo) en localStorage
          try {
            clearNamespace(NAMESPACE)
          } catch (e) {
            // fallback: si falla el borrado namespaced, no borrar todo para evitar perder datos globales
            // eslint-disable-next-line no-console
            console.warn('clearNamespace failed, skipping global clear:', e)
          }
          if (window.sessionStorage) window.sessionStorage.clear()
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('storage clear failed:', e)
      }

      // Borrar CacheStorage (caches) usado por service workers / assets precacheados
      try {
        if (typeof caches !== 'undefined' && typeof caches.keys === 'function') {
          const cacheKeys = await caches.keys()
          await Promise.all(cacheKeys.map(key => caches.delete(key)))
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('CacheStorage clear failed:', e)
      }

      // Si se usa localforage/indexedDB, intentar limpiarlo dinámicamente
      try {
        // import dinámico para no forzar la dependencia si no está instalada
        // esto fallará silenciosamente si no existe
        // eslint-disable-next-line import/no-extraneous-dependencies
        const lf = await import('localforage')
        if (lf && typeof lf.clear === 'function') await lf.clear()
      } catch (e) {
        // noop: library not present or clear failed
      }
      // Limpiar cookies de la app (prefijo por NAMESPACE)
      try {
        if (typeof document !== 'undefined') {
          const NAMESPACE =
            (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STORAGE_NAMESPACE) ||
            'BUP'
          const cookies = document.cookie ? document.cookie.split(';') : []
          for (const cookie of cookies) {
            const [rawName] = cookie.split('=')
            const name = rawName && rawName.trim()
            if (!name) continue
            if (name.startsWith(`${NAMESPACE}_`) || name.startsWith(`${NAMESPACE}:`)) {
              document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${location.hostname}`
            }
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('cookie clear failed:', e)
      }
      dispatch({ type: 'success', payload: data })
    } catch (error) {
      dispatch({ type: 'error', payload: error })
    }
  }

  return { ...state, mutate }
}
